import {useEffect, useState} from 'react'
import {Alert, Button, Form, Spinner} from 'react-bootstrap'

export interface IEmailSetupValues {
  emailMode?: string | null
  emailHost?: string | null
  emailPort?: number | null
  emailHostUser?: string | null
  emailHostPassword?: string | null
  emailUseTls?: boolean | null
  defaultFromEmail?: string | null
  publicRequireIdentity?: boolean | null
}

interface IResolvedSources {
  emailMode?: string | null
  emailHost?: string | null
  emailPort?: string | null
  emailHostUser?: string | null
  emailUseTls?: string | null
  defaultFromEmail?: string | null
}

interface IResolvedEmailSetup {
  emailMode?: string | null
  emailHost?: string | null
  emailPort?: number | null
  emailHostUser?: string | null
  emailUseTls?: boolean | null
  defaultFromEmail?: string | null
  hasEmailHostPassword?: boolean | null
  sources?: IResolvedSources
}

interface IEmailSetupSettings {
  emailMode?: string | null
  emailHost?: string | null
  emailPort?: number | null
  emailHostUser?: string | null
  emailUseTls?: boolean | null
  defaultFromEmail?: string | null
  hasEmailHostPassword?: boolean | null
}

interface Props {
  values: IEmailSetupSettings | null | undefined
  resolved?: IResolvedEmailSetup | null
  showInheritance?: boolean
  saving?: boolean
  onSave: (input: IEmailSetupValues) => void | Promise<void>
  // Solo se muestra en el nivel Sistema (SuperAdmin) — si se pasa, se agrega
  // el switch de PUBLIC_REQUIRE_IDENTITY y su valor viaja en el mismo Guardar.
  publicRequireIdentity?: boolean | null
  showPublicRequireIdentity?: boolean
  // Si se pasa, se agrega un input + botón para enviar un email de prueba con
  // la configuración ya guardada (no la del formulario sin guardar).
  onSendTestEmail?: (toEmail: string) => void | Promise<void>
  sendingTestEmail?: boolean
}

export const EmailSetupForm: React.FC<Props> = ({
  values,
  resolved,
  showInheritance = false,
  saving = false,
  onSave,
  publicRequireIdentity,
  showPublicRequireIdentity = false,
  onSendTestEmail,
  sendingTestEmail = false,
}) => {
  const [testEmailTo, setTestEmailTo] = useState('')
  const [form, setForm] = useState({
    emailMode: values?.emailMode || '',
    emailHost: values?.emailHost || '',
    emailPort: values?.emailPort != null ? String(values.emailPort) : '',
    emailHostUser: values?.emailHostUser || '',
    emailHostPassword: '',
    emailUseTls: values?.emailUseTls ?? true,
    defaultFromEmail: values?.defaultFromEmail || '',
    publicRequireIdentity: Boolean(publicRequireIdentity),
  })
  const [overriding, setOverriding] = useState(
    Boolean(values?.emailMode || values?.emailHost || values?.emailUseTls !== null)
  )
  // DEFAULT_FROM_EMAIL se puede overridear independientemente del resto del
  // transporte SMTP: en AWS SES, por ejemplo, todas las organizaciones pueden
  // compartir el mismo usuario/credenciales y solo cambiar la dirección "from"
  // (que debe estar verificada en SES) por organización.
  const [overridingFrom, setOverridingFrom] = useState(Boolean(values?.defaultFromEmail))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setForm({
      emailMode: values?.emailMode || '',
      emailHost: values?.emailHost || '',
      emailPort: values?.emailPort != null ? String(values.emailPort) : '',
      emailHostUser: values?.emailHostUser || '',
      emailHostPassword: '',
      emailUseTls: values?.emailUseTls ?? true,
      defaultFromEmail: values?.defaultFromEmail || '',
      publicRequireIdentity: Boolean(publicRequireIdentity),
    })
    setOverriding(Boolean(values?.emailMode || values?.emailHost || values?.emailUseTls !== null))
    setOverridingFrom(Boolean(values?.defaultFromEmail))
  }, [values?.emailMode, values?.emailHost, values?.emailPort, values?.emailHostUser, values?.emailUseTls, values?.defaultFromEmail, publicRequireIdentity])

  const isOverridden = !showInheritance || overriding
  const isOverriddenFrom = !showInheritance || overridingFrom

  const inheritedHint = (label: string, value: any) => {
    if (!showInheritance || !resolved) return null
    if (value === null || value === undefined || value === '') return `Inherited from upper level`
    return `Inherited: ${label} = ${String(value)}`
  }

  const handleTlsChange = (checked: boolean) => {
    setForm((p) => ({
      ...p,
      emailUseTls: checked,
      emailPort: !p.emailPort || p.emailPort === '587' || p.emailPort === '25'
        ? (checked ? '587' : '25')
        : p.emailPort,
    }))
  }

  const handleSave = () => {
    setError(null)

    if (isOverriddenFrom && !form.defaultFromEmail) {
      setError('DEFAULT_FROM_EMAIL is required when overriding it.')
      return
    }

    const input: IEmailSetupValues = {
      defaultFromEmail: isOverriddenFrom ? form.defaultFromEmail : null,
      ...(showPublicRequireIdentity ? {publicRequireIdentity: form.publicRequireIdentity} : {}),
    }

    if (!isOverridden) {
      onSave({
        emailMode: null,
        emailHost: null,
        emailPort: null,
        emailHostUser: null,
        emailUseTls: null,
        ...input,
      })
      return
    }

    const passwordMissing = !form.emailHostPassword && !values?.hasEmailHostPassword
    if (!form.emailMode || !form.emailHost || !form.emailPort || !form.emailHostUser || passwordMissing) {
      setError('All SMTP transport fields are required when overriding this configuration.')
      return
    }

    input.emailMode = form.emailMode
    input.emailHost = form.emailHost
    input.emailPort = Number(form.emailPort)
    input.emailHostUser = form.emailHostUser
    input.emailUseTls = form.emailUseTls
    if (form.emailHostPassword) {
      input.emailHostPassword = form.emailHostPassword
    }

    onSave(input)
  }

  return (
    <div>
      {showInheritance && (
        <div className='d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom'>
          <span className='text-muted fs-7'>
            Fields below inherit the system default configuration unless overridden.
          </span>
          <Form.Check
            type='switch'
            id='override-all'
            label='Override'
            checked={overriding}
            onChange={(e) => setOverriding(e.target.checked)}
          />
        </div>
      )}

      {error && (
        <Alert variant='danger' className='py-2'>
          <small>{error}</small>
        </Alert>
      )}

      <Form.Group className='mb-4'>
        <Form.Label className='fw-bold'>Delivery Mode</Form.Label>
        <Form.Select
          value={form.emailMode}
          onChange={(e) => {
            const mode = e.target.value
            setForm((p) => ({
              ...p,
              emailMode: mode,
              ...(mode === 'gmail'
                ? {
                    emailHost: p.emailHost || 'smtp.gmail.com',
                    emailPort: p.emailPort || '587',
                    emailUseTls: true,
                  }
                : {}),
            }))
          }}
          disabled={!isOverridden}
        >
          <option value=''>{isOverridden ? 'Select...' : inheritedHint('mode', resolved?.emailMode)}</option>
          <option value='smtp'>SMTP</option>
          <option value='gmail'>Gmail</option>
        </Form.Select>
      </Form.Group>

      <h6 className='fw-bold mb-3'>SMTP Server</h6>

      <div className='row align-items-start mb-3'>
        <Form.Group className='mb-3 col-md-5'>
          <Form.Label className='fw-semibold'>EMAIL_HOST</Form.Label>
          <Form.Control
            value={form.emailHost}
            onChange={(e) => setForm((p) => ({...p, emailHost: e.target.value}))}
            disabled={!isOverridden}
            placeholder={!isOverridden ? inheritedHint('host', resolved?.emailHost) || '' : 'e.g. smtp.gmail.com'}
          />
        </Form.Group>
        <Form.Group className='mb-3 col-md-3'>
          <Form.Label className='fw-semibold'>EMAIL_PORT</Form.Label>
          <Form.Control
            type='number'
            value={form.emailPort}
            onChange={(e) => setForm((p) => ({...p, emailPort: e.target.value}))}
            disabled={!isOverridden}
            placeholder={!isOverridden ? inheritedHint('port', resolved?.emailPort) || '' : (form.emailUseTls ? '587' : '25')}
          />
        </Form.Group>
        <Form.Group className='mb-3 col-md-4'>
          <Form.Label className='fw-semibold mb-2'>EMAIL_USE_TLS</Form.Label>
          <div className='d-flex align-items-center' style={{ minHeight: '38px' }}>
            <Form.Check
              type='switch'
              id='emailUseTls'
              label={form.emailUseTls ? 'Use TLS (Port 587)' : 'No TLS (Port 25)'}
              checked={Boolean(form.emailUseTls)}
              onChange={(e) => handleTlsChange(e.target.checked)}
              disabled={!isOverridden}
            />
          </div>
        </Form.Group>
      </div>

      <div className='row'>
        <Form.Group className='mb-4 col-md-6'>
          <Form.Label className='fw-semibold'>EMAIL_HOST_USER</Form.Label>
          <Form.Control
            value={form.emailHostUser}
            onChange={(e) => setForm((p) => ({...p, emailHostUser: e.target.value}))}
            disabled={!isOverridden}
            placeholder={!isOverridden ? inheritedHint('user', resolved?.emailHostUser) || '' : ''}
          />
        </Form.Group>
        <Form.Group className='mb-4 col-md-6'>
          <Form.Label className='fw-semibold'>
            EMAIL_HOST_PASSWORD{' '}
            {values?.hasEmailHostPassword && <span className='text-muted fs-8'>(already configured — leave empty to keep unchanged)</span>}
          </Form.Label>
          <Form.Control
            type='password'
            value={form.emailHostPassword}
            onChange={(e) => setForm((p) => ({...p, emailHostPassword: e.target.value}))}
            disabled={!isOverridden}
            placeholder={
              !isOverridden
                ? resolved?.hasEmailHostPassword
                  ? 'Inherited from upper level (configured)'
                  : 'Inherited from upper level (not configured)'
                : '••••••••'
            }
          />
        </Form.Group>
      </div>

      <Form.Group className='mb-4'>
        <div className='d-flex align-items-center justify-content-between mb-1'>
          <Form.Label className='fw-bold mb-0'>DEFAULT_FROM_EMAIL</Form.Label>
          {showInheritance && (
            <Form.Check
              type='switch'
              id='override-from'
              label='Override'
              checked={overridingFrom}
              onChange={(e) => setOverridingFrom(e.target.checked)}
            />
          )}
        </div>
        {showInheritance && (
          <span className='text-muted fs-8 d-block mb-2'>
            Useful when sending through the same SMTP user (e.g. AWS SES) but with a different verified "from" address.
          </span>
        )}
        <Form.Control
          value={form.defaultFromEmail}
          onChange={(e) => setForm((p) => ({...p, defaultFromEmail: e.target.value}))}
          disabled={!isOverriddenFrom}
          placeholder={!isOverriddenFrom ? inheritedHint('default from', resolved?.defaultFromEmail) || '' : ''}
        />
      </Form.Group>

      {showPublicRequireIdentity && (
        <Form.Group className='mb-4 pt-3 border-top'>
          <Form.Check
            type='switch'
            id='publicRequireIdentity'
            label='PUBLIC_REQUIRE_IDENTITY — Require identity verification on public forms'
            checked={form.publicRequireIdentity}
            onChange={(e) => setForm((p) => ({...p, publicRequireIdentity: e.target.checked}))}
          />
          <span className='text-muted fs-8 d-block mt-1'>
            Global default for public forms. A form token can still override this value individually.
          </span>
        </Form.Group>
      )}

      <div className='d-flex align-items-center justify-content-between pt-2 border-top'>
        {onSendTestEmail ? (
          <div className='d-flex align-items-center gap-2'>
            <Form.Control
              type='email'
              size='sm'
              placeholder='Send test email to...'
              value={testEmailTo}
              onChange={(e) => setTestEmailTo(e.target.value)}
              style={{width: '220px'}}
            />
            <Button
              variant='light'
              size='sm'
              onClick={() => onSendTestEmail(testEmailTo)}
              disabled={sendingTestEmail || !testEmailTo}
            >
              {sendingTestEmail ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' />
                  Sending...
                </>
              ) : (
                'Send test email'
              )}
            </Button>
          </div>
        ) : (
          <span />
        )}
        <Button variant='primary' onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner animation='border' size='sm' className='me-2' />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </div>
  )
}
